/**
 * Pure monophonic pitch detector (McLeod NSDF / MPM).
 *
 * - Normalised square difference function, so the result does not depend on signal level.
 * - Relative peak threshold (K * highest key maximum), first qualifying peak wins.
 * - Parabolic interpolation on the NSDF values (not on waveform samples).
 * - Octave guard against sub-harmonic picks.
 * - Fast path: the coarse search runs on a boxcar-decimated copy of the frame, then the winning lag is
 *   refined on the full-rate signal over a handful of lags. A 4096-sample frame costs well under 1 ms.
 *
 * No DOM or Web Audio dependency: it can be imported from Node specs.
 */

export interface PitchResult {
  /** Fundamental frequency in Hz. */
  frequency: number;
  /** NSDF peak height, 0..1. Higher means a cleaner periodic signal. */
  clarity: number;
}

export interface DetectPitchOptions {
  /** Lowest frequency to look for (Hz). Default 50. */
  minHz?: number;
  /** Highest frequency to look for (Hz). Default 1200. */
  maxHz?: number;
  /** Frames quieter than this RMS (after DC removal) are treated as silence. Default 0.004. */
  rmsFloor?: number;
  /** Minimum NSDF peak height to accept a pitch. Default 0.6. */
  minClarity?: number;
  /** Relative key-maximum threshold (MPM "k"). Default 0.9. */
  k?: number;
}

/** NSDF at one integer lag, summed over the whole frame. */
function nsdfAt(x: Float32Array | Float64Array, tau: number): number {
  const n = x.length - tau;
  let acf = 0;
  let m = 0;
  for (let j = 0; j < n; j++) {
    const a = x[j];
    const b = x[j + tau];
    acf += a * b;
    m += a * a + b * b;
  }
  return m > 0 ? (2 * acf) / m : 0;
}

/** Vertex of the least-squares parabola through 2*half+1 equally spaced samples centred on index c. */
function parabolicFit(v: Float32Array, c: number, half: number): { offset: number; peak: number } {
  let s2 = 0, s4 = 0, t0 = 0, t1 = 0, t2 = 0, s0 = 0;
  for (let k = -half; k <= half; k++) {
    const yv = v[c + k];
    s0 += 1; s2 += k * k; s4 += k * k * k * k;
    t0 += yv; t1 += k * yv; t2 += k * k * yv;
  }
  // y = a + b k + c2 k^2 (odd sums vanish by symmetry)
  const det = s0 * s4 - s2 * s2;
  const c2 = (s0 * t2 - s2 * t0) / det;
  const a = (s4 * t0 - s2 * t2) / det;
  const b = t1 / s2;
  if (!(c2 < 0) || !isFinite(c2)) return parabolic(v[c - 1], v[c], v[c + 1]);
  const off = Math.max(-1, Math.min(1, -b / (2 * c2)));
  return { offset: off, peak: a + b * off + c2 * off * off };
}

/** Vertex of the parabola through three equally spaced samples; offset is in [-0.5, 0.5]. */
function parabolic(ym: number, y0: number, yp: number): { offset: number; peak: number } {
  const denom = ym - 2 * y0 + yp;
  if (denom >= 0 || !isFinite(denom)) return { offset: 0, peak: y0 };
  const offset = (0.5 * (ym - yp)) / denom;
  const clamped = Math.max(-0.5, Math.min(0.5, offset));
  return { offset: clamped, peak: y0 - 0.25 * (ym - yp) * clamped };
}

/** Half-width (in full-rate lags) of the least-squares fit used for the final refinement. */
const FIT_HALF_WIDTH = 3;

export function detectPitch(
  input: Float32Array,
  sampleRate: number,
  options: DetectPitchOptions = {}
): PitchResult | null {
  const minHz = options.minHz ?? 50;
  const maxHz = options.maxHz ?? 1200;
  const rmsFloor = options.rmsFloor ?? 0.004;
  const minClarity = options.minClarity ?? 0.6;
  const K = options.k ?? 0.9;
  const N = input.length;
  if (N < 64 || !(sampleRate > 0)) return null;

  // DC removal and level check
  let mean = 0;
  for (let i = 0; i < N; i++) mean += input[i];
  mean /= N;
  const x = new Float32Array(N);
  let energy = 0;
  for (let i = 0; i < N; i++) {
    const v = input[i] - mean;
    x[i] = v;
    energy += v * v;
  }
  if (Math.sqrt(energy / N) < rmsFloor) return null;

  // Coarse search on a decimated copy (~5 samples per period at maxHz is plenty for peak picking)
  const d = Math.max(1, Math.floor(sampleRate / (5 * maxHz)));
  let y: Float32Array = x;
  if (d > 1) {
    y = new Float32Array(Math.floor(N / d));
    for (let i = 0; i < y.length; i++) {
      let acc = 0;
      const base = i * d;
      for (let k = 0; k < d; k++) acc += x[base + k];
      y[i] = acc / d;
    }
  }
  const rate = sampleRate / d;
  const minLag = Math.max(2, Math.floor(rate / maxHz));
  // Need at least two periods of the lowest note inside the frame
  const maxLag = Math.min(Math.ceil(rate / minHz), Math.floor(y.length / 2) - 2);
  if (maxLag <= minLag + 2) return null;

  const nsdf = new Float32Array(maxLag + 2);
  for (let tau = 0; tau <= maxLag + 1; tau++) nsdf[tau] = nsdfAt(y, tau);

  // Skip the initial positive lobe around lag 0
  let start = 1;
  while (start < maxLag && nsdf[start] > 0) start++;
  start = Math.max(start, minLag);

  // Key maxima: one per positive region
  const keyLags: number[] = [];
  let best = 0;
  let i = start;
  while (i <= maxLag) {
    if (nsdf[i] > 0) {
      let peak = i;
      while (i <= maxLag && nsdf[i] > 0) {
        if (nsdf[i] > nsdf[peak]) peak = i;
        i++;
      }
      // Only genuine local maxima, not a lobe still rising at the search boundary
      if (peak > start && peak < maxLag && nsdf[peak] >= nsdf[peak - 1] && nsdf[peak] >= nsdf[peak + 1]) {
        keyLags.push(peak);
        if (nsdf[peak] > best) best = nsdf[peak];
      }
    } else {
      i++;
    }
  }
  if (keyLags.length === 0 || best < minClarity) return null;

  const threshold = K * best;
  let chosen = keyLags[0];
  for (const lag of keyLags) {
    if (nsdf[lag] >= threshold) {
      chosen = lag;
      break;
    }
  }

  // Octave guard: if half the lag is a strong peak of its own, the true period is the shorter one
  const half = Math.round(chosen / 2);
  if (half >= minLag + 1) {
    let hp = half;
    for (let l = half - 1; l <= half + 1; l++) if (nsdf[l] > nsdf[hp]) hp = l;
    if (hp > minLag && nsdf[hp] >= 0.85 * nsdf[chosen] && nsdf[hp] >= nsdf[hp - 1] && nsdf[hp] >= nsdf[hp + 1]) {
      chosen = hp;
    }
  }

  // Refine on the full-rate signal around the coarse lag
  let lag: number;
  let clarity: number;
  if (d > 1) {
    const centre = chosen * d;
    const lo = Math.max(2, centre - d - 1);
    const hi = Math.min(Math.floor(N / 2), centre + d + 1);
    const vals = new Float32Array(hi - lo + 1);
    let bi = 0;
    for (let l = lo; l <= hi; l++) {
      vals[l - lo] = nsdfAt(x, l);
      if (vals[l - lo] > vals[bi]) bi = l - lo;
    }
    if (bi === 0 || bi === vals.length - 1) return null;
    const half = Math.min(FIT_HALF_WIDTH, bi, vals.length - 1 - bi);
    const p = half >= 2 ? parabolicFit(vals, bi, half) : parabolic(vals[bi - 1], vals[bi], vals[bi + 1]);
    lag = lo + bi + p.offset;
    clarity = p.peak;
  } else {
    const p = parabolic(nsdf[chosen - 1], nsdf[chosen], nsdf[chosen + 1]);
    lag = chosen + p.offset;
    clarity = p.peak;
  }
  if (clarity < minClarity) return null;

  const frequency = sampleRate / lag;
  if (frequency < minHz * 0.9 || frequency > maxHz * 1.1) return null;
  return { frequency, clarity: Math.min(1, clarity) };
}
